/*
 * /website-design-dallas — PRIMARY Dallas SEO landing page.
 *
 * Built to Kit's Aug 2026 SEO brief exactly:
 *   - H1 pinned: "Dallas Website Design That Turns Visitors Into Customers"
 *   - Price statement: "$600–$2,000"
 *   - No invented reviews / awards / partnerships / media appearances
 *   - No competing agency named on-page
 *   - Concept work labelled "concept"
 *   - Server-rendered content: title, meta desc, canonical, robots, OG, JSON-LD
 *   - Distinct intent from /ai-website-design-dallas (AI-focused) —
 *     this page targets "Dallas web design" broadly; that page stays.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LIGHT_BG } from "@/lib/lightTheme";
import {
  Phone, ArrowRight, CheckCircle2, Users, MapPin, Rocket, Palette, Bot,
  Shield, Award, Calendar as CalIcon, ClipboardList, CreditCard, Search,
  Smartphone, LayoutDashboard,
} from "lucide-react";

function useDallasSeo() {
  useEffect(() => {
    document.title = "Dallas Website Design & AI Sales Systems | PlugitAI";
    const set = (attr, k, v) => {
      let el = document.querySelector(`meta[${attr}="${k}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, k); document.head.appendChild(el); }
      el.setAttribute("content", v);
    };
    set("name", "description", "Custom Dallas website design with built-in AI sales agents, local SEO and lead automation. Most PlugitAI projects range from $600–$2,000. Call (214) 815-5966.");
    set("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1");
    set("property", "og:title", "Dallas Website Design & AI Sales Systems | PlugitAI");
    set("property", "og:description", "Custom Dallas web design + AI sales agents. Projects $600–$2,000. (214) 815-5966.");
    set("property", "og:url", "https://plugitai.com/website-design-dallas");
    set("property", "og:type", "website");
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", "https://plugitai.com/website-design-dallas");
  }, []);
}

// Server-rendered JSON-LD payload (Kit's spec: LocalBusiness / ProfessionalService / Service / FAQPage / BreadcrumbList)
const JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": "https://plugitai.com/website-design-dallas#business",
    "name": "PlugitAI",
    "url": "https://plugitai.com/website-design-dallas",
    "telephone": "+1-214-815-5966",
    "priceRange": "$600–$2,000",
    "areaServed": { "@type": "City", "name": "Dallas" },
    "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
    "founder": { "@type": "Person", "name": "Chris (Kit) Garcia" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Dallas Website Design",
    "provider": { "@id": "https://plugitai.com/website-design-dallas#business" },
    "areaServed": "Dallas, TX",
    "offers": { "@type": "AggregateOffer", "lowPrice": "600", "highPrice": "2000", "priceCurrency": "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",                item: "https://plugitai.com/" },
      { "@type": "ListItem", "position": 2, "name": "Website Design Dallas", item: "https://plugitai.com/website-design-dallas" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How much does a Dallas website cost?", "acceptedAnswer": { "@type": "Answer", "text": "Most PlugitAI projects for Dallas businesses range from $600 to $2,000 depending on scope, integrations and the AI modules you turn on. Payment options are available." }},
      { "@type": "Question", "name": "How long does a Dallas web design project take?", "acceptedAnswer": { "@type": "Answer", "text": "Standard launches ship in 2–4 weeks. Custom builds with deeper AI workflows run 4–8 weeks depending on scope." }},
      { "@type": "Question", "name": "Do I own my website?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. You own your domain, content and design. Hosting and AI-agent runtime are billed transparently and can be moved if you leave." }},
      { "@type": "Question", "name": "Do you work with businesses outside Dallas?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — but the core team is in DFW, which means faster in-person meetings and Dallas-specific local SEO for clients in the metroplex." }},
    ],
  },
];

// ─── Small reusable UI atoms ────────────────────────────────────────────
function Section({ id, eyebrow, h2, children }) {
  return (
    <section id={id} className="max-w-[1160px] mx-auto px-6 md:px-10 py-14 md:py-20">
      {eyebrow && <div className="text-[10px] uppercase tracking-[0.32em] font-black text-blue-600 mb-3">{eyebrow}</div>}
      {h2 && <h2 className="font-black text-2xl md:text-3xl tracking-[-0.02em]" style={{ fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>{h2}</h2>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function CTA({ to, primary, label, icon: Icon = ArrowRight, testId, href }) {
  const cn = "inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] transition-colors "
    + (primary ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-300 hover:border-slate-500 text-slate-900");
  return href
    ? <a href={href} className={cn} data-testid={testId}>{label} <Icon size={14} /></a>
    : <Link to={to} className={cn} data-testid={testId}>{label} <Icon size={14} /></Link>;
}

// ─── Page ──────────────────────────────────────────────────────────────
export default function WebsiteDesignDallas() {
  useDallasSeo();
  const [form, setForm] = useState({ name: "", phone: "", email: "", detail: "" });
  const [sent, setSent] = useState(false);
  const submit = (e) => { e.preventDefault(); /* wired in a follow-up — for now the tel: CTA is the primary conversion */ setSent(true); };

  return (
    <main className="min-h-screen text-slate-900" style={LIGHT_BG} data-testid="page-website-design-dallas">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JSONLD) }} />

      {/* 1. Hero */}
      <header className="max-w-[1160px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-8" data-testid="hero">
        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] font-black text-blue-600 mb-4">
          Dallas · Fort Worth · DFW
        </div>
        <h1 className="font-black leading-[1.02] tracking-[-0.02em]"
            style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)", fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}
            data-testid="hero-h1">
          Dallas Website Design That Turns Visitors Into{" "}
          <span
            className="italic"
            style={{
              fontFamily: "'Fraunces','Instrument Serif','Playfair Display',Georgia,serif",
              backgroundImage: "linear-gradient(135deg, #1E5FE0 0%, #22C55E 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Customers.
          </span>
        </h1>
        <p className="mt-5 max-w-[720px] text-base md:text-lg text-slate-600 leading-relaxed">
          Build more than an online brochure. PlugitAI creates custom Dallas business websites that answer leads, qualify customers, schedule appointments, collect information and help businesses compete in local search.
        </p>
        <div className="mt-4 text-sm text-slate-500" data-testid="hero-price">
          Most projects range from <span className="text-slate-900 font-black">$600–$2,000</span>. Payment options are available.
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#plan" className="inline-flex items-center gap-2 h-12 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] bg-blue-600 text-white hover:bg-blue-700"
             data-testid="cta-primary">Get My Website Plan <ArrowRight size={14} /></a>
          <a href="tel:+12148155966" className="inline-flex items-center gap-2 h-12 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] border border-slate-300 hover:border-slate-500"
             data-testid="cta-call"><Phone size={14} /> (214) 815-5966</a>
        </div>
      </header>

      {/* 2. Trust — real 4-person in-house team */}
      <Section eyebrow="Real people · Dallas-based" h2="A four-person, in-house Dallas team.">
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { name: "Chris (Kit) Garcia", role: "Founder · Design + Sales", d: "One of 4 certified Google Experts in Texas (2017)." },
            { name: "Design",             role: "Interface + brand",        d: "In-house — no offshore hand-off." },
            { name: "Engineering",         role: "Site build + AI wiring",   d: "Ships in weeks, not quarters." },
            { name: "SEO + Ops",           role: "Local search + growth",    d: "Structured for Dallas ranking signals." },
          ].map((m) => (
            <div key={m.name} className="rounded-lg border border-slate-200 p-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="text-sm font-black">{m.name}</div>
              <div className="text-[11px] text-blue-600 uppercase tracking-[0.2em] mt-1">{m.role}</div>
              <div className="text-[12px] text-slate-500 mt-2">{m.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 3. Not just a website */}
      <Section eyebrow="What actually ships" h2="Not just a website — a customer engine.">
        <div className="grid md:grid-cols-2 gap-3">
          {[
            [Bot,           "AI receptionist",         "Answers chats and calls 24/7 in your business voice."],
            [ClipboardList, "Lead qualification",       "Asks the right questions — timeline, budget, intent."],
            [CalIcon,       "Appointment scheduling",   "Books straight into your calendar from the chat."],
            [Rocket,        "Quote-request collection", "Captures the exact scope, no back-and-forth."],
            [CreditCard,    "Approved payment collection", "Stripe checkout on approved offers only."],
            [Search,        "Local SEO structure",      "Schema, sitemap, city + service targeting done right."],
            [Smartphone,    "Mobile-first design",      "Real usability on phones — where Dallas customers actually search."],
            [LayoutDashboard,"Admin dashboard",         "One panel: leads, conversations, appointments, billing."],
          ].map(([I, t, d]) => (
            <div key={t} className="rounded-lg border border-slate-200 p-4 bg-white flex items-start gap-3 bg-white">
              <I size={16} className="text-blue-600 shrink-0 mt-1" />
              <div>
                <div className="text-sm font-black">{t}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 4. Comparison — no competitor named */}
      <Section eyebrow="Compare" h2="Traditional Dallas agency vs. PlugitAI.">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 font-black">Traditional Dallas agency</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>Approximately $2,490+ for five pages</li>
              <li>Website is primarily informational</li>
              <li>Automation usually costs extra</li>
              <li>Limited SEO content</li>
              <li>Longer development process</li>
            </ul>
          </div>
          <div className="rounded-lg border-2 border-blue-500 p-5 bg-blue-50">
            <div className="text-[10px] uppercase tracking-[0.28em] text-blue-600 font-black">PlugitAI</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-800">
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Typically $600–$2,000</li>
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Custom website and AI sales functionality</li>
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Local SEO foundation</li>
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Lead capture and automation</li>
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Industry and service content based on the agreed scope</li>
              <li className="flex gap-2"><CheckCircle2 size={14} className="text-blue-600 shrink-0 mt-1"/>Direct access to a Dallas-based team</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 5. Real portfolio */}
      <Section eyebrow="Real client sites" h2="Live Dallas / DFW work.">
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { name: "Lone Star Striping Pros", url: "https://lonestarstriping.pro/", tag: "Live · Fort Worth" },
            { name: "iHeartFrenchies",         url: null,                             tag: "Concept · not launched", concept: true },
            { name: "Miss Rodriguez",          url: null,                             tag: "Concept · not launched", concept: true },
          ].map((c) => (
            <div key={c.name} className="rounded-lg border border-slate-200 p-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="text-sm font-black">{c.name}</div>
              <div className={`text-[10px] uppercase tracking-[0.24em] mt-1 ${c.concept ? "text-amber-500" : "text-emerald-600"}`}>
                {c.tag}
              </div>
              {c.url ? (
                <a href={c.url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[11px] uppercase tracking-[0.22em] font-black text-blue-600 hover:text-blue-700">
                  Visit live site →
                </a>
              ) : (
                <div className="mt-3 text-[11px] text-slate-400">Awaiting client go-live approval.</div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 text-[11px] text-slate-400">Concept builds are labelled as concepts. No mockup is presented as a completed client engagement.</div>
      </Section>

      {/* 6. Case studies — labelled, verified only */}
      <Section eyebrow="Case studies" h2="Documented outcomes.">
        <div className="grid md:grid-cols-2 gap-3">
          <Link to="/case-studies/lone-star-striping" className="rounded-lg border border-slate-200 p-5 bg-white hover:border-blue-500 hover:bg-slate-50 block">
            <div className="text-sm font-black">Lone Star Striping Pros</div>
            <div className="text-[11px] text-blue-600 mt-1 uppercase tracking-[0.22em]">Fort Worth · Case study →</div>
            <div className="text-[12px] text-slate-500 mt-2">Full 50-page site + AI SalesBot. Numbers, dates and methodology documented on the case-study page.</div>
          </Link>
          <div className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] opacity-70">
            <div className="text-sm font-black">More case studies in progress</div>
            <div className="text-[12px] text-slate-500 mt-2">We only publish outcomes we can document with screenshots, dates and methodology. New case studies added as client permissions come through.</div>
          </div>
        </div>
      </Section>

      {/* 7. Dallas industries */}
      <Section eyebrow="Dallas industries" h2="Who we build for.">
        <div className="flex flex-wrap gap-2">
          {["Contractors","Healthcare","Automotive","Professional services","E-commerce","Local service businesses"].map((i) => (
            <span key={i} className="px-3 py-1.5 rounded-full border border-slate-300 text-[11px] uppercase tracking-[0.2em] font-black text-slate-600">{i}</span>
          ))}
        </div>
      </Section>

      {/* 8. Process */}
      <Section eyebrow="How the build runs" h2="Discovery → Strategy → Design → Development → Launch → Growth.">
        <div className="grid md:grid-cols-6 gap-2">
          {[
            ["Discovery","Understand the business, offers and ideal customer."],
            ["Strategy","Site map, keyword targets, AI conversation design."],
            ["Design","Brand-true UI shipped from Dallas."],
            ["Development","Real code — no bloated page builder."],
            ["Launch","Site live, AI trained, analytics wired."],
            ["Growth","SEO monitoring, iteration, follow-ups."],
          ].map(([t, d]) => (
            <div key={t} className="rounded-lg border border-slate-200 p-3 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="text-[10px] uppercase tracking-[0.24em] text-blue-600 font-black">{t}</div>
              <div className="text-[11px] text-slate-500 mt-1">{d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 9. Pricing + ownership */}
      <Section eyebrow="Transparent" h2="Pricing and what you own.">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">You own</div>
            <ul className="mt-2 space-y-1 text-[13px] text-slate-600">
              <li>· Your domain</li>
              <li>· All content and design</li>
              <li>· All customer records</li>
              <li>· Exported CMS if you leave</li>
            </ul>
          </div>
          <div className="rounded-lg border border-amber-300 p-5 bg-amber-50">
            <div className="text-sm font-black">Ongoing costs (disclosed up front)</div>
            <ul className="mt-2 space-y-1 text-[13px] text-slate-600">
              <li>· Hosting + AI agent runtime — flat monthly, quoted at scope</li>
              <li>· Optional SEO monitoring + support</li>
              <li>· No hidden fees, no lock-ins</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* 10. Dallas FAQs */}
      <Section id="faq" eyebrow="FAQ" h2="Dallas-specific questions.">
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["How much does a Dallas website cost?", "Most PlugitAI projects range from $600–$2,000 depending on scope and AI modules. Payment options available."],
            ["How long does a Dallas project take?", "Standard launches ship in 2–4 weeks; deeper builds run 4–8 weeks."],
            ["Do I own my website?", "Yes — domain, content, design and customer records are all yours."],
            ["Do you work with clients outside Dallas?", "Yes, but our core team is in DFW so Dallas clients get faster meetings and metroplex-tuned local SEO."],
          ].map(([q, a]) => (
            <div key={q} className="rounded-lg border border-slate-200 p-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="text-sm font-black">{q}</div>
              <div className="text-[13px] text-slate-600 mt-2">{a}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 11. Final CTA + short lead form */}
      <Section id="plan" eyebrow="Get started" h2="Get your Dallas website plan.">
        {sent ? (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-5 text-sm" data-testid="plan-sent">
            Thanks — Kit will reach out from (214) 815-5966. If it&apos;s urgent, tap the call button now.
          </div>
        ) : (
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3 max-w-[720px]" data-testid="plan-form">
            <input required placeholder="Your name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
                   className="h-11 rounded-md bg-white border border-slate-300 px-3 text-sm outline-none focus:border-blue-500" data-testid="plan-name" />
            <input required placeholder="Phone" type="tel" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}
                   className="h-11 rounded-md bg-white border border-slate-300 px-3 text-sm outline-none focus:border-blue-500" data-testid="plan-phone" />
            <input placeholder="Email (optional)" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
                   className="h-11 rounded-md bg-white border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 md:col-span-2" data-testid="plan-email" />
            <textarea placeholder="What kind of business?" value={form.detail} onChange={(e)=>setForm({...form,detail:e.target.value})}
                      rows={3}
                      className="rounded-md bg-white border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 md:col-span-2" data-testid="plan-detail" />
            <div className="md:col-span-2 flex flex-wrap gap-3">
              <button type="submit" className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] bg-blue-600 text-white hover:bg-blue-700" data-testid="plan-submit">
                Get My Website Plan <ArrowRight size={14} />
              </button>
              <a href="tel:+12148155966" className="inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] border border-slate-300 hover:border-slate-500" data-testid="plan-call">
                <Phone size={14} /> (214) 815-5966
              </a>
            </div>
          </form>
        )}
      </Section>

      {/* Internal links — supporting Dallas pages */}
      <Section eyebrow="Related" h2="Explore more.">
        <div className="grid md:grid-cols-3 gap-3">
          {[
            ["/ai-website-design-dallas", "AI Website Design Dallas", "AI-first Dallas builds."],
            ["/custom-web-design-dallas",  "Custom Web Design Dallas", "Bespoke Dallas brand experiences."],
            ["/website-launch",             "Website Launch tier",       "$299.99 launch tier for fastest go-live."],
            ["/custom-studio",              "Custom Studio",             "Bespoke design + six-bot AI workforce."],
            ["/case-studies/lone-star-striping", "Lone Star Case Study", "Verified Fort Worth build."],
            ["/about-kit",                  "About Kit",                 "The Dallas builder behind PlugitAI."],
          ].map(([to, t, d]) => (
            <Link key={to} to={to} className="rounded-lg border border-slate-200 p-4 bg-white hover:border-blue-500 hover:bg-slate-50 block" data-testid={`related-${to}`}>
              <div className="text-sm font-black">{t}</div>
              <div className="text-[12px] text-slate-500 mt-1">{d}</div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Author + reviewer + last-updated */}
      <div className="max-w-[1160px] mx-auto px-6 md:px-10 pb-16 text-[11px] text-slate-400 border-t border-slate-200 pt-6" data-testid="page-authorship">
        Author: Chris (Kit) Garcia · Reviewer: PlugitAI editorial · Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </div>
    </main>
  );
}
